import * as Sentry from '@sentry/react';
import {
  createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useGetAllVeevaFatalErrorsLazy from 'basics/graphql/queries/getAllVeevaFatalErrorsLazy';
import useLazyGetMayaUserData from 'basics/graphql/queries/getMayaUserDataLazy';
import { CurrentUser } from 'basics/types/user.types';
import { isLocal } from 'config/config';
import OktaClient from 'services/okta';
import veevaFatalErrorsState from 'states/veevaFatalErrors.states';

// `undefined` as loading user
// `null` as NOT logged in user
// `CurrentUser` as logged in user

type CurrentUserState = {
  currentUser?: CurrentUser | null;
  loading?: boolean;
};

type CurrentUserContext = {
  currentUserData: CurrentUserState;
  setCurrentUserData: Dispatch<SetStateAction<CurrentUserState>>;
};

const initialState = {
  currentUserData: {
    currentUser: undefined,
    loading: false,
  },
  setCurrentUserData: () => {},
};

export const CurrentUserStateContext = createContext<CurrentUserContext>(initialState);

type CurrentUserProviderProps = {
  children: ReactNode;
};

type UseCurrentUserState = () => {
  user: CurrentUser | null | undefined;
  userLoading: boolean;
};

export const useCurrentUserState: UseCurrentUserState = () => {
  const context = useContext(CurrentUserStateContext);
  const { currentUserData: { currentUser, loading } } = context;
  return {
    user: currentUser,
    userLoading: !!loading,
  };
};

export const CurrentUserProvider: FC<CurrentUserProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { queryGetMayaUserData } = useLazyGetMayaUserData();
  const { getAllVeevaFatalErrorsLazy } = useGetAllVeevaFatalErrorsLazy();

  const [currentUserData, setCurrentUserData] = useState<CurrentUserState>(
    {
      currentUser: undefined,
      loading: true,
    },
  );

  async function checkUser() {
    const userInfos = await OktaClient.getUserInfo();

    if (!userInfos?.user && currentUserData.currentUser !== undefined) {
      setCurrentUserData({ currentUser: undefined, loading: false });
    }
  }

  useEffect(() => {
    OktaClient.watchAuth(async (userInfos, comesFromLogin, redirectTo) => {
      if (userInfos?.user) {
        const { user } = userInfos;
        try {
          const mayaUserData = await queryGetMayaUserData();
          const { data } = await getAllVeevaFatalErrorsLazy();

          if (data.getAllVeevaFatalErrors.length > 0) {
            veevaFatalErrorsState.hasErrors = true;
          }

          setCurrentUserData(
            { currentUser: {
              ...user,
              country: mayaUserData.data?.getUserData?.country,
              dashboardFilters: mayaUserData.data?.getUserData?.dashboardFilters,
              adminDashboardFilters: mayaUserData.data?.getUserData?.adminDashboardFilters,
            },
            loading: false },
          );
        } catch (error) {
          setCurrentUserData(
            { currentUser: { ...user, country: null, dashboardFilters: null, adminDashboardFilters: null }, loading: false },
          );
        }

        if (!isLocal) {
          Sentry.setUser({ user: user.id, email: user.email });
        }

        if (comesFromLogin) {
          navigate(redirectTo || '/', { replace: true });
        }
      } else {
        setCurrentUserData({ currentUser: undefined, loading: false });
      }
    });

    window.addEventListener('storage', () => {
      const oktaToken = window.localStorage.getItem('okta_token.token');
      if (!oktaToken || !JSON.parse(oktaToken).accessToken) {
        setCurrentUserData({ currentUser: undefined, loading: false });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkUser();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = {
    currentUserData,
    setCurrentUserData,
  };
  return (
    <CurrentUserStateContext.Provider value={ contextValue }>
      { children }
    </CurrentUserStateContext.Provider>
  );
};
